import {installAiosSelects} from "./aios_select"
import { floatingListboxPosition } from "./floating_listbox_position.mjs"

// Minimum width for the floating panel: a picker's trigger is often as narrow
// as the value it shows (`w-16` on a coefficient row), and the SEARCH box is
// what has to stay usable, not the trigger.
const MIN_PANEL_WIDTH = 248

const normalize = value =>
  (value ?? "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()

// Mogno's searchable dropdown, worn by a NATIVE `<select>`.
//
// `SeivaWeb.Components.SearchableSelect` is the same widget driven by the
// server: it owns open/query state and four events per field. That contract is
// right for a picker whose options come from a query, and impossible for the
// forty enumeration fields of a hub — forty times four handlers to gain a
// search box. This hook is the other half of the same standard: the `<select>`
// STAYS in the DOM as the single source of truth (it serializes into the form,
// it answers `phx-change`, it keeps its `required` validation), and the hook
// only paints the Mogno panel over it and writes the chosen value back with a
// real `change` event. No server state, no new events, no changed handlers.
//
// The panel is portalled to `document.body` on purpose: everything LiveView
// renders inside the hook root stays server-owned, so a patch can never fight
// the panel, and no ancestor's `overflow` can clip it.
const SearchableSelect = {
  mounted() {
    if (document.documentElement.classList.contains("aios-document")) {this.platformSelect=installAiosSelects();return}

    this.panel = null
    this.search = null
    this.query = ""
    this.activeIndex = -1
    this.positionFrame = null

    this.handleDocumentPointerDown = event => this.onDocumentPointerDown(event)
    this.handleViewportChange = () => this.schedulePosition()
    this.handleFocusIn = event => this.onFocusIn(event)

    this.bind()
    document.addEventListener("pointerdown", this.handleDocumentPointerDown, true)
    document.addEventListener("focusin", this.handleFocusIn, true)
    window.addEventListener("resize", this.handleViewportChange)
    window.addEventListener("scroll", this.handleViewportChange, true)

    this.syncTriggerLabel()
  },

  updated() {
    if(this.platformSelect){this.platformSelect.sync();return}

    this.bind()
    this.syncTriggerLabel()
    // The server may have replaced the option set under an open panel (a
    // dependent field: pick a statement, the row list changes). Repaint rather
    // than close — closing would lose the search the person is in the middle of.
    if (this.panel) this.renderOptions()
  },

  destroyed() {
    if(this.platformSelect){this.platformSelect.close();return}

    document.removeEventListener("pointerdown", this.handleDocumentPointerDown, true)
    document.removeEventListener("focusin", this.handleFocusIn, true)
    window.removeEventListener("resize", this.handleViewportChange)
    window.removeEventListener("scroll", this.handleViewportChange, true)
    if (this.positionFrame !== null) cancelAnimationFrame(this.positionFrame)
    this.closePanel({ focusTrigger: false })
  },

  bind() {
    const select = this.el.querySelector("select")
    const trigger = this.el.querySelector("[data-searchable-trigger]")

    if (select !== this.select) {
      this.select?.removeEventListener("change", this.handleSelectChange)
      this.select = select
      this.handleSelectChange = () => this.syncTriggerLabel()
      this.select?.addEventListener("change", this.handleSelectChange)
    }

    if (trigger !== this.trigger) {
      this.trigger?.removeEventListener("click", this.handleTriggerClick)
      this.trigger?.removeEventListener("keydown", this.handleTriggerKeydown)
      this.trigger = trigger
      this.handleTriggerClick = event => this.onTriggerClick(event)
      this.handleTriggerKeydown = event => this.onTriggerKeydown(event)
      this.trigger?.addEventListener("click", this.handleTriggerClick)
      this.trigger?.addEventListener("keydown", this.handleTriggerKeydown)
    }
  },

  // --- state read from the native select -----------------------------------

  entries() {
    if (!this.select) return []

    return Array.from(this.select.options).map((option, index) => ({
      index,
      value: option.value,
      label: option.textContent.trim(),
      group: option.parentElement?.tagName === "OPTGROUP" ? option.parentElement.label : null,
      disabled: option.disabled,
      selected: option.selected,
      // `data-prompt` marks the "nothing chosen yet" option. It reads as a
      // placeholder, not as a value — same as the server decides for the
      // trigger it renders.
      prompt: option.dataset.prompt !== undefined
    }))
  },

  visibleEntries() {
    const needle = normalize(this.query)
    if (needle === "") return this.entries()

    return this.entries().filter(
      entry => normalize(entry.label).includes(needle) || normalize(entry.group).includes(needle)
    )
  },

  selectedEntry() {
    return this.entries().find(entry => entry.selected) || null
  },

  disabled() {
    return Boolean(this.select?.disabled)
  },

  // The trigger is server-rendered so the field reads right before JS runs and
  // in a LiveView test's DOM; from here on the hook keeps it honest, including
  // the case LiveView cannot patch (the value changed while the trigger held
  // focus).
  syncTriggerLabel() {
    const label = this.el.querySelector("[data-searchable-label]")
    if (!label) return

    const selected = this.selectedEntry()
    const placeholder = this.el.dataset.placeholder || ""
    const empty = !selected || selected.label === "" || selected.prompt
    const text = empty ? (selected && selected.label) || placeholder : selected.label

    if (label.textContent !== text) label.textContent = text
    label.dataset.placeholderShown = String(empty)
    this.trigger?.setAttribute("aria-expanded", String(Boolean(this.panel)))
  },

  // --- open / close --------------------------------------------------------

  onTriggerClick(event) {
    event.preventDefault()
    if (this.disabled()) return
    this.panel ? this.closePanel() : this.openPanel()
  },

  onTriggerKeydown(event) {
    if (this.disabled()) return

    if (["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) {
      event.preventDefault()
      if (!this.panel) this.openPanel()
    }
  },

  openPanel() {
    if (this.panel || !this.trigger) return

    this.query = ""

    const panel = document.createElement("div")
    panel.className = "seiva-searchable-panel"
    panel.setAttribute("role", "dialog")
    panel.id = `${this.el.id}-panel`
    panel.dataset.testid = `${this.el.dataset.testid || this.el.id}-panel`
    panel.style.position = "fixed"
    panel.style.zIndex = "2147483647"
    panel.style.visibility = "hidden"

    const form = document.createElement("div")
    form.className = "seiva-searchable-panel__search"

    const icon = document.createElement("span")
    icon.className = "feather-search seiva-searchable-panel__search-icon"
    icon.setAttribute("aria-hidden", "true")

    const search = document.createElement("input")
    search.type = "text"
    search.className = "seiva-searchable-panel__search-input"
    search.placeholder = this.el.dataset.searchPlaceholder || ""
    search.setAttribute("aria-label", this.el.dataset.searchPlaceholder || "")
    search.setAttribute("autocomplete", "off")
    search.dataset.testid = `${this.el.dataset.testid || this.el.id}-search`
    // A LiveView form serializes every named input inside it. The search box
    // must never reach `phx-change`, so it carries no `name` at all.
    search.addEventListener("input", () => {
      this.query = search.value
      this.renderOptions()
      this.schedulePosition()
    })
    search.addEventListener("keydown", event => this.onSearchKeydown(event))

    form.appendChild(icon)
    form.appendChild(search)

    const list = document.createElement("div")
    list.className = "seiva-searchable-panel__list"
    list.setAttribute("role", "listbox")
    list.addEventListener("pointerdown", event => event.preventDefault())
    list.addEventListener("click", event => this.onListClick(event))
    list.addEventListener("pointermove", event => this.onListPointerMove(event))

    panel.appendChild(form)
    panel.appendChild(list)
    document.body.appendChild(panel)

    this.panel = panel
    this.search = search
    this.list = list

    this.renderOptions()
    this.positionPanel()
    search.focus()
    this.trigger.setAttribute("aria-expanded", "true")
  },

  closePanel({ focusTrigger = true } = {}) {
    if (!this.panel) return

    this.panel.remove()
    this.panel = null
    this.search = null
    this.list = null
    this.activeIndex = -1
    this.query = ""
    this.trigger?.setAttribute("aria-expanded", "false")
    if (focusTrigger) this.trigger?.focus()
  },

  onDocumentPointerDown(event) {
    if (!this.panel) return
    if (this.el.contains(event.target) || this.panel.contains(event.target)) return
    this.closePanel({ focusTrigger: false })
  },

  // A picker left open while the person tabs into the next field is a picker
  // covering that field.
  onFocusIn(event) {
    if (!this.panel) return
    if (this.panel.contains(event.target) || this.el.contains(event.target)) return
    this.closePanel({ focusTrigger: false })
  },

  // --- option list ---------------------------------------------------------

  renderOptions() {
    if (!this.list) return

    const entries = this.visibleEntries()
    this.list.textContent = ""

    if (entries.length === 0) {
      const empty = document.createElement("p")
      empty.className = "seiva-searchable-panel__empty"
      empty.textContent = this.el.dataset.emptyLabel || ""
      this.list.appendChild(empty)
      this.activeIndex = -1
      return
    }

    let renderedGroup = null

    entries.forEach((entry, position) => {
      if (entry.group && entry.group !== renderedGroup) {
        const header = document.createElement("p")
        header.className = "seiva-searchable-panel__group"
        header.textContent = entry.group
        this.list.appendChild(header)
      }
      renderedGroup = entry.group

      const option = document.createElement("button")
      option.type = "button"
      option.className = "seiva-searchable-panel__option"
      option.setAttribute("role", "option")
      option.setAttribute("aria-selected", String(entry.selected))
      option.dataset.index = String(entry.index)
      option.dataset.position = String(position)
      option.dataset.selected = String(entry.selected)
      if (entry.disabled) option.disabled = true

      const label = document.createElement("span")
      label.className = "seiva-searchable-panel__option-label"
      // An empty-valued first option is the field's prompt ("Select company").
      // It stays in the list — it is how a non-required field is cleared.
      label.textContent = entry.label === "" ? this.el.dataset.placeholder || "—" : entry.label
      option.appendChild(label)

      const check = document.createElement("span")
      check.className = "feather-check seiva-searchable-panel__check"
      check.setAttribute("aria-hidden", "true")
      option.appendChild(check)

      this.list.appendChild(option)
    })

    const selectedPosition = entries.findIndex(entry => entry.selected)
    this.activeIndex = selectedPosition >= 0 ? selectedPosition : 0
    this.paintActive({ scroll: true })
  },

  optionElements() {
    return this.list ? Array.from(this.list.querySelectorAll(".seiva-searchable-panel__option")) : []
  },

  paintActive({ scroll = false } = {}) {
    const options = this.optionElements()
    if (options.length === 0) {
      this.activeIndex = -1
      this.search?.removeAttribute("aria-activedescendant")
      return
    }

    this.activeIndex = Math.min(Math.max(this.activeIndex, 0), options.length - 1)

    options.forEach((option, position) => {
      option.dataset.active = String(position === this.activeIndex)
    })

    if (scroll) options[this.activeIndex].scrollIntoView({ block: "nearest" })
  },

  onListClick(event) {
    const option = event.target.closest(".seiva-searchable-panel__option")
    if (!option || option.disabled) return

    event.preventDefault()
    this.commit(Number.parseInt(option.dataset.index, 10))
  },

  onListPointerMove(event) {
    const option = event.target.closest(".seiva-searchable-panel__option")
    if (!option) return

    const position = Number.parseInt(option.dataset.position || "", 10)
    if (!Number.isInteger(position) || position === this.activeIndex) return

    this.activeIndex = position
    this.paintActive()
  },

  onSearchKeydown(event) {
    const options = this.optionElements()

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        if (options.length === 0) return
        this.activeIndex = (this.activeIndex + 1) % options.length
        this.paintActive({ scroll: true })
        break
      case "ArrowUp":
        event.preventDefault()
        if (options.length === 0) return
        this.activeIndex = (this.activeIndex - 1 + options.length) % options.length
        this.paintActive({ scroll: true })
        break
      case "Home":
        event.preventDefault()
        this.activeIndex = 0
        this.paintActive({ scroll: true })
        break
      case "End":
        event.preventDefault()
        this.activeIndex = options.length - 1
        this.paintActive({ scroll: true })
        break
      case "Enter": {
        event.preventDefault()
        const active = options[this.activeIndex]
        if (active && !active.disabled) this.commit(Number.parseInt(active.dataset.index, 10))
        break
      }
      case "Escape":
        event.preventDefault()
        event.stopPropagation()
        this.closePanel()
        break
      case "Tab":
        this.closePanel({ focusTrigger: false })
        break
      default:
        break
    }
  },

  // --- writing back --------------------------------------------------------

  // The whole point: the value lands on the NATIVE select and the events a
  // `<select>` would have fired are fired for real, so `phx-change` forms,
  // `phx-change` on the select itself and plain form submits all keep working
  // exactly as they did before the picker existed.
  commit(optionIndex) {
    if (!this.select || !Number.isInteger(optionIndex)) return

    const option = this.select.options[optionIndex]
    if (!option || option.disabled) return

    const changed = this.select.selectedIndex !== optionIndex
    this.select.selectedIndex = optionIndex

    this.closePanel()
    this.syncTriggerLabel()

    if (!changed) return

    this.select.dispatchEvent(new Event("input", { bubbles: true }))
    this.select.dispatchEvent(new Event("change", { bubbles: true }))
  },

  // --- positioning ---------------------------------------------------------

  schedulePosition() {
    if (!this.panel || this.positionFrame !== null) return

    this.positionFrame = requestAnimationFrame(() => {
      this.positionFrame = null
      this.positionPanel()
    })
  },

  positionPanel() {
    if (!this.panel || !this.trigger) return

    const rect = this.trigger.getBoundingClientRect()
    const boundary = { left: 0, right: window.innerWidth, top: 0, bottom: window.innerHeight }

    if (rect.bottom <= boundary.top || rect.top >= boundary.bottom) {
      this.panel.style.visibility = "hidden"
      return
    }

    const anchor = {
      left: rect.left,
      right: rect.right,
      top: rect.top,
      bottom: rect.bottom,
      width: Math.max(rect.width, MIN_PANEL_WIDTH)
    }

    this.panel.style.visibility = "hidden"
    this.panel.style.maxHeight = "320px"
    const position = floatingListboxPosition(anchor, boundary, this.panel.scrollHeight, {
      maxHeight: 320,
      preferredHeight: 200
    })

    Object.assign(this.panel.style, {
      left: `${Math.round(position.left)}px`,
      top: `${Math.round(position.top)}px`,
      width: `${Math.round(position.width)}px`,
      maxHeight: `${Math.max(0, Math.floor(position.maxHeight))}px`,
      visibility: "visible"
    })

    this.panel.dataset.placement = position.placement
  }
}

export default SearchableSelect
